// ABOUTME: Dataset picker component for selecting a sample/demo dataset during onboarding.
// ABOUTME: Shows cards for Databricks sample datasets with descriptions and example questions.

import { AlertCircle, ArrowLeft, Database, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { DemoDatasetId } from '@/types/api';

interface DatasetOption {
  id: DemoDatasetId;
  name: string;
  description: string;
  rowCount: string;
  exampleQuestions: string[];
}

const DATASET_OPTIONS: DatasetOption[] = [
  {
    id: 'accuweather',
    name: 'Weather Data',
    description: 'AccuWeather station data with forecasts, temperatures, and precipitation',
    rowCount: '~4M rows',
    exampleQuestions: [
      'What are the average temperatures by city over the past year?',
      'Which cities experience the most precipitation annually?',
      'Show me temperature trends across seasons for major US cities',
    ],
  },
  {
    id: 'bakehouse',
    name: 'Bakery Sales',
    description: 'Bakery sales transactions with products, quantities, and revenue',
    rowCount: '~250K rows',
    exampleQuestions: [
      'What are the top-selling bakery products by revenue?',
      'Show me daily sales patterns across the week',
      'Which product categories have the highest profit margins?',
    ],
  },
  {
    id: 'healthverity',
    name: 'Healthcare Claims',
    description: 'De-identified healthcare claims with diagnoses, procedures, and pharmacy data',
    rowCount: '~10M rows',
    exampleQuestions: [
      'What are the most common diagnosis codes by patient age group?',
      'Show me the distribution of claim amounts by procedure type',
      'Which specialties generate the highest claim volumes?',
    ],
  },
  {
    id: 'nyctaxi',
    name: 'NYC Taxi Trips',
    description: 'NYC taxi trip records with fares, locations, and tips',
    rowCount: '~1.4B rows',
    exampleQuestions: [
      'What is the average fare by pickup borough?',
      'Show me hourly trip volume patterns across a typical week',
      'Which routes have the highest average tips?',
    ],
  },
  {
    id: 'tpcds_sf1',
    name: 'Retail Sales (Small)',
    description: 'TPC-DS retail benchmark data with stores, web, and catalog sales at scale factor 1',
    rowCount: '~6M rows',
    exampleQuestions: [
      'What are the top 10 stores by total revenue?',
      'Compare web sales vs catalog sales vs store sales by quarter',
      'Which product categories have the highest return rates?',
    ],
  },
  {
    id: 'tpcds_sf1000',
    name: 'Retail Sales (Large)',
    description: 'TPC-DS retail benchmark data with stores, web, and catalog sales at scale factor 1000',
    rowCount: '~6B rows',
    exampleQuestions: [
      'What are the top 10 stores by total revenue?',
      'Compare web sales vs catalog sales vs store sales by quarter',
      'Which product categories have the highest return rates?',
    ],
  },
  {
    id: 'tpch',
    name: 'E-commerce & Supply Chain',
    description: 'TPC-H benchmark data -- orders, customers, suppliers across 25 nations',
    rowCount: '~1.5M rows',
    exampleQuestions: [
      'What are the top 10 customers by total order value?',
      'Show me monthly revenue trends over the past year',
      'Which suppliers have the longest average delivery times?',
    ],
  },
  {
    id: 'wanderbricks',
    name: 'Travel & Hotels',
    description: 'Travel booking data with hotels, destinations, ratings, and pricing',
    rowCount: '~500K rows',
    exampleQuestions: [
      'What are the top-rated hotel destinations by average review score?',
      'Show me booking volume trends by season and destination',
      'Which hotel categories offer the best value for price?',
    ],
  },
];

interface SampleDatasetPickerProps {
  onSelect: (datasetId: DemoDatasetId) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export function SampleDatasetPicker({
  onSelect,
  onCancel,
  isLoading = false,
  error = null,
}: SampleDatasetPickerProps) {
  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isLoading}
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-surface-900 mb-2">
          Choose a Sample Dataset
        </h2>
        <p className="text-surface-600">
          Explore Obsidian BI with real data. You can connect your own data later.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-sm bg-error/10 border border-error/20 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Dataset Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {DATASET_OPTIONS.map((dataset) => (
          <div key={dataset.id} className="p-5 rounded-sm border border-surface-200 bg-surface-0 flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <Database className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-surface-900">
                  {dataset.name}
                </h3>
                <span className="inline-block mt-0.5 text-xs font-medium text-surface-500 bg-surface-100 px-2 py-0.5 rounded">
                  {dataset.rowCount}
                </span>
              </div>
            </div>

            <p className="text-sm text-surface-600 mb-3">
              {dataset.description}
            </p>

            {/* Example Questions */}
            <div className="space-y-1.5 mb-4">
              <span className="text-xs font-medium text-surface-400 uppercase tracking-wider">
                Example questions
              </span>
              {dataset.exampleQuestions.map((question) => (
                <p key={question} className="text-sm text-surface-500 pl-3 border-l-2 border-surface-200">
                  {question}
                </p>
              ))}
            </div>

            <div className="mt-auto">
              <Button
                variant="primary"
                size="sm"
                onClick={() => onSelect(dataset.id)}
                disabled={isLoading}
                aria-label={`Select ${dataset.name}`}
                className="w-full"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Select'
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
